import { useState, useEffect } from "react";
import "./AddVendor.css";
import dataService from "../services/dataService";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../context/ToastContext";

function AddVendorScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    taxId: "",
    website: "",
    address: "",
    bankName: "",
    accountNumber: "",
    iban: "",
    swiftCode: "",
    status: "Active",
    category: "",
    paymentTerms: "Net 30",
    creditLimit: 50000,
    contactPersonName: "",
    contactPersonEmail: "",
    contactPersonPhone: ""
  });

  useEffect(() => {
    if (isEditMode) {
      const fetchVendor = async () => {
        try {
          const response = await dataService.getVendors();
          const vendor = response.data.data.find(v => v._id === id);
          if (vendor) {
            setFormData({
              name: vendor.name || "",
              company: vendor.company || "",
              email: vendor.email || "",
              phone: vendor.phone || "",
              taxId: vendor.taxId || "",
              website: vendor.website || "",
              address: vendor.address || "",
              bankName: vendor.bankName || "",
              accountNumber: vendor.accountNumber || "",
              iban: vendor.iban || "",
              swiftCode: vendor.swiftCode || "",
              status: vendor.status || "Active",
              category: vendor.category || "",
              paymentTerms: vendor.paymentTerms || "Net 30",
              creditLimit: vendor.creditLimit || 50000,
              contactPersonName: vendor.contactPersonName || "",
              contactPersonEmail: vendor.contactPersonEmail || "",
              contactPersonPhone: vendor.contactPersonPhone || ""
            });
          } else {
            setError("Vendor not found");
          }
        } catch (err) {
          setError("Failed to fetch vendor details");
        } finally {
          // done
        }
      };
      fetchVendor();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEditMode) {
        await dataService.updateVendor(id, formData);
        addToast("Vendor updated successfully", "success");
      } else {
        await dataService.addVendor(formData);
        addToast("Vendor created successfully", "success");
      }
      navigate("/vendors");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create vendor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>{isEditMode ? "Edit Vendor" : "Add New Vendor"}</h1>
        <p>{isEditMode ? `Updating ${formData.name}` : "Create a new vendor profile"}</p>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Vendor Information</h2>
              <div className="v-name">
                <div>
                  <p>Vendor Name</p>
                  <input name="name" value={formData.name} onChange={handleChange} required placeholder="enter vendor name" />
                </div>
                <div>
                  <p>Company Name</p>
                  <input name="company" value={formData.company} onChange={handleChange} placeholder="Enter company name" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Email Address</p>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="vendor@example.com" />
                </div>
                <div>
                  <p>Phone Number</p>
                  <input name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 234 567 8900" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Tax ID/VAT</p>
                  <input name="taxId" value={formData.taxId} onChange={handleChange} placeholder="TAX-XXX-XXX" />
                </div>
                <div>
                  <p>Website</p>
                  <input name="website" value={formData.website} onChange={handleChange} placeholder="https://vendor-website.com" />
                </div>
              </div>

              <div>
                <p>Address</p>
                <input name="address" value={formData.address} onChange={handleChange} placeholder="Street Address, City, State, zip" />
              </div>
            </div>

            <div className="vendor-card">
              <h2>Banking Details</h2>
              <div className="v-name">
                <div>
                  <p>Bank Name</p>
                  <input name="bankName" value={formData.bankName} onChange={handleChange} placeholder="enter bank name" />
                </div>
                <div>
                  <p>Account Number</p>
                  <input name="accountNumber" value={formData.accountNumber} onChange={handleChange} placeholder="XXXX-XXXX-XXXX" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>IBAN</p>
                  <input name="iban" value={formData.iban} onChange={handleChange} placeholder="GBXX XXXX XXXX XXXX XXXX XX" />
                </div>
                <div>
                  <p>Swift/BIC code</p>
                  <input name="swiftCode" value={formData.swiftCode} onChange={handleChange} placeholder="XXXXXXXXXX" />
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Vendor Settings</h2>
                <div>
                  <div>
                    <p>Status</p>
                    <select name="status" value={formData.status} onChange={handleChange}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div>
                    <p>Vendor Category</p>
                    <input list="category" name="category" value={formData.category} onChange={handleChange} placeholder="select category" />
                    <datalist id="category">
                      <option value="Electronics" />
                      <option value="Office Supplies" />
                      <option value="Raw materials" />
                      <option value="Services" />
                      <option value="Others" />
                    </datalist>
                  </div>
                  <p>Payment Terms</p>
                  <select name="paymentTerms" value={formData.paymentTerms} onChange={handleChange}>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Net 90">Net 90</option>
                  </select>
                  <p>Credit Limit</p>
                  <input type="number" name="creditLimit" value={formData.creditLimit} onChange={handleChange} placeholder="50000" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Contact Person</h2>
                <p>Name</p>
                <input name="contactPersonName" value={formData.contactPersonName} onChange={handleChange} placeholder="Contact person name" />
                <p>Email</p>
                <input type="email" name="contactPersonEmail" value={formData.contactPersonEmail} onChange={handleChange} placeholder="contact@example.com" />
                <p>Phone</p>
                <input name="contactPersonPhone" value={formData.contactPersonPhone} onChange={handleChange} placeholder="+1 234 567 8900" />
              </div>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/vendors")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Saving..." : (isEditMode ? "Update Vendor" : "Create Vendor")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddVendorScreen;
